import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Copy, Key, Code, Zap, Book, ArrowRight, ExternalLink, AlertCircle, Shield, Users } from 'lucide-react';
import { useAuth } from '../lib/auth.tsx';
import { supabase } from '../lib/db.ts';

const QuickStart = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null);

  useEffect(() => {
    if (user) {
      checkAccountStatus();
    }
  }, [user]);

  const checkAccountStatus = async () => {
    try {
      const { data } = await supabase
        .from('developer_accounts')
        .select('status')
        .eq('user_id', user?.id)
        .single();

      if (data) {
        setAccountStatus(data.status);
      }
    } catch {
      // Account doesn't exist yet, that's fine
    }
  };

  const copyToClipboard = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getStep1Content = () => (
    <div className="space-y-4">
      <p className="text-wayja-dark-slate">
        To get started with Wayja's API, you'll need to create a developer account. This will give you access to:
      </p>
      <ul className="list-disc list-inside text-wayja-dark-slate space-y-1">
        <li>API key for authentication</li>
        <li>Developer dashboard for monitoring usage</li>
        <li>Documentation and support resources</li>
        <li>Test environment access</li>
      </ul>

      {accountStatus === 'approved' ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-green-800">Account Already Registered</h4>
              <p className="text-green-700 text-sm mt-1">
                Your developer account has been approved! View your API keys and manage your account.{' '}
                <Link to="/developers/register" className="underline font-medium">
                  View API Keys →
                </Link>
              </p>
            </div>
          </div>
        </div>
      ) : accountStatus === 'pending' ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-blue-800">Registration Pending</h4>
              <p className="text-blue-700 text-sm mt-1">
                Your developer account registration is pending approval. You will receive an email once approved.{' '}
                <Link to="/developers/register" className="underline font-medium">
                  Check Status →
                </Link>
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-green-800">Ready to Get Started?</h4>
              <p className="text-green-700 text-sm mt-1">
                Register for a developer account to get your API key and access the test environment.{' '}
                <Link to="/developers/register" className="underline font-medium">
                  Register Now →
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const steps = [
    {
      id: 1,
      title: "Create Developer Account",
      description: "Register for a developer account to get your API key and access credentials.",
      icon: <Key className="w-6 h-6" />,
      getContent: getStep1Content
    },
    {
      id: 2,
      title: "Set Up Authentication",
      description: "Configure your API key and understand authentication requirements.",
      icon: <Shield className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-wayja-dark-slate">
            Wayja uses API key authentication for all requests. Include your key in the X-API-Key header:
          </p>

          <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400">Header Format</span>
              <button
                onClick={() => copyToClipboard('X-API-Key: YOUR_API_KEY', 'auth-header')}
                className="text-gray-400 hover:text-white"
              >
                {copiedCode === 'auth-header' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <pre>X-API-Key: YOUR_API_KEY</pre>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">HMAC Request Signing</h4>
            <p className="text-blue-700 text-sm">
              <strong>Important:</strong> State-modifying endpoints (place-bet, wallet-topup, etc.) require HMAC request signing 
              using your signing secret. Read-only endpoints (market-data) only need API key authentication.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-800">Security Note</h4>
            <p className="text-yellow-700 text-sm mt-1">
              Keep your API key and signing secret secure. Never expose them in client-side code or public repositories.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 3,
      title: "Make Your First API Call",
      description: "Test your setup by fetching market data from the API.",
      icon: <Code className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-wayja-dark-slate">
            Let's test your API key by fetching data for a sample market. Replace <code className="bg-wayja-cream px-1 rounded">YOUR_API_KEY</code> with your actual key:
          </p>

          <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400">Test Market Data</span>
              <button
                onClick={() => copyToClipboard(`curl -H "X-API-Key: YOUR_API_KEY" \\
  https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/market-data/market-sample`, 'test-curl')}
                className="text-gray-400 hover:text-white"
              >
                {copiedCode === 'test-curl' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <pre>{`curl -H "X-API-Key: YOUR_API_KEY" \\
  https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/market-data/market-sample`}</pre>
          </div>

          <p className="text-gray-600">
            You should receive a JSON response with market details, options, and current odds.
          </p>
        </div>
      )
    },
    {
      id: 4,
      title: "Implement User Authentication",
      description: "Set up user authentication for betting operations.",
      icon: <Users className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            For operations that require user authentication (like placing trades), users must authenticate directly with Wayja.
            This ensures proper security, user consent, and compliance with financial regulations.
          </p>


          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">Authentication Flow</h4>
            <ol className="text-sm text-blue-800 list-decimal list-inside space-y-2">
              <li>Users log in directly to Wayja (via <code className="bg-blue-100 px-1 rounded">wayja.me</code> or OAuth 2.0 redirect)</li>
              <li>After successful authentication, Wayja issues a signed session token (<code className="bg-blue-100 px-1 rounded">user_token</code>)</li>
              <li>Use this token in API requests for user-specific operations</li>
              <li>For external platforms/widgets, implement OAuth 2.0 redirect flow to Wayja login page</li>
            </ol>
          </div>

          <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400">OAuth 2.0 Redirect Example</span>
              <button
                onClick={() => copyToClipboard(`// Redirect users to Wayja login
const redirectUri = encodeURIComponent(window.location.href);
const wayjaLoginUrl = \`https://wayja.me/auth/login?redirect_uri=\${redirectUri}\`;
window.location.href = wayjaLoginUrl;

// After login, Wayja redirects back with user_token in URL
// Extract token and use in subsequent API calls`, 'oauth-example')}
                className="text-gray-400 hover:text-white"
              >
                {copiedCode === 'oauth-example' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <pre>{`// Redirect users to Wayja login
const redirectUri = encodeURIComponent(window.location.href);
const wayjaLoginUrl = \`https://wayja.me/auth/login?redirect_uri=\${redirectUri}\`;
window.location.href = wayjaLoginUrl;

// After login, Wayja redirects back with user_token in URL
// Extract token and use in subsequent API calls`}</pre>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-3">🧪 Test the OAuth Flow</h4>
            <p className="text-green-700 text-sm mb-3">
              Create a simple HTML file to test the OAuth redirect flow. Save this as <code className="bg-green-100 px-1 rounded">test-oauth.html</code>:
            </p>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono mb-3 overflow-x-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400">Complete Test Page (with full token display & copy)</span>
                <button
                  onClick={() => copyToClipboard(`<!DOCTYPE html>
<html>
<head>
  <title>Wayja OAuth Test</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      max-width: 800px;
      margin: 50px auto;
      padding: 20px;
      background-color: #f5f5f5;
    }
    .container {
      background: white;
      padding: 30px;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    h1 {
      color: #333;
      margin-bottom: 20px;
    }
    #status {
      padding: 15px;
      background-color: #e3f2fd;
      border-left: 4px solid #2196f3;
      margin-bottom: 20px;
      border-radius: 4px;
    }
    #status strong {
      color: #1976d2;
    }
    button {
      background-color: #00b4a6;
      color: white;
      border: none;
      padding: 12px 24px;
      font-size: 16px;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.3s;
    }
    button:hover {
      background-color: #009688;
    }
    #tokenDisplay {
      margin-top: 30px;
      padding: 20px;
      background-color: #f9f9f9;
      border-radius: 4px;
      border: 1px solid #e0e0e0;
    }
    #tokenDisplay h3 {
      margin-top: 0;
      color: #333;
    }
    #tokenDisplay code {
      background-color: #f0f0f0;
      padding: 2px 6px;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      font-size: 14px;
    }
    .success {
      color: #4caf50;
      font-weight: bold;
    }
    .token-container {
      background-color: #f5f5f5;
      padding: 15px;
      border-radius: 4px;
      margin: 10px 0;
      word-break: break-all;
      position: relative;
    }
    .token-text {
      font-family: 'Courier New', monospace;
      font-size: 12px;
      color: #333;
      margin: 0;
      padding-right: 80px;
    }
    .copy-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      background-color: #00b4a6;
      color: white;
      border: none;
      padding: 6px 12px;
      font-size: 12px;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.3s;
    }
    .copy-btn:hover {
      background-color: #009688;
    }
    .copy-btn.copied {
      background-color: #4caf50;
    }
    .show-full-btn {
      background-color: #2196f3;
      color: white;
      border: none;
      padding: 6px 12px;
      font-size: 12px;
      border-radius: 4px;
      cursor: pointer;
      margin-top: 10px;
      transition: background-color 0.3s;
    }
    .show-full-btn:hover {
      background-color: #1976d2;
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>Wayja OAuth 2.0 Test</h1>
    
    <div id="status">Ready to authenticate...</div>
    <button id="loginBtn" onclick="redirectToLogin()">Login with Wayja</button>
    <div id="tokenDisplay"></div>
  </div>

  <script>
    let fullTokenVisible = false;
    let storedToken = null;
    let storedUserId = null;

    // Check if we're returning from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const userToken = urlParams.get('wayja_token');
    const userId = urlParams.get('wayja_user_id');

    if (userToken && userId) {
      storedToken = userToken;
      storedUserId = userId;
      displayToken(userToken, userId);
      
      // Clean URL (remove token for security)
      window.history.replaceState({}, '', window.location.pathname);
    }

    function displayToken(token, userId) {
      document.getElementById('status').innerHTML = 
        '<strong class="success">✓ Authentication successful!</strong>';
      
      const tokenDisplay = document.getElementById('tokenDisplay');
      tokenDisplay.innerHTML = 
        '<h3>Token Received:</h3>' +
        '<p><strong>User ID:</strong> ' + userId + '</p>' +
        '<div class="token-container">' +
        '<p class="token-text" id="tokenText">' + (fullTokenVisible ? token : token.substring(0, 50) + '...') + '</p>' +
        '<button class="copy-btn" onclick="copyToken()" id="copyBtn">Copy Token</button>' +
        '</div>' +
        '<button class="show-full-btn" onclick="toggleTokenDisplay()" id="toggleBtn">' + 
        (fullTokenVisible ? 'Show Less' : 'Show Full Token') + 
        '</button>' +
        '<p class="success" style="margin-top: 15px;">You can now use this token in API calls!</p>' +
        '<p style="margin-top: 15px; font-size: 14px; color: #666;">' +
        '<strong>Example API call:</strong><br>' +
        '<code style="display: block; margin-top: 5px; padding: 10px; background: #f0f0f0; border-radius: 4px; word-break: break-all;">' +
        'fetch("https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/wallet-balance?user_token=" + token, {<br>' +
        '&nbsp;&nbsp;headers: { "X-API-Key": "YOUR_API_KEY" }<br>' +
        '})' +
        '</code></p>';
    }

    function toggleTokenDisplay() {
      fullTokenVisible = !fullTokenVisible;
      const tokenText = document.getElementById('tokenText');
      const toggleBtn = document.getElementById('toggleBtn');
      
      if (storedToken) {
        tokenText.textContent = fullTokenVisible ? storedToken : storedToken.substring(0, 50) + '...';
        toggleBtn.textContent = fullTokenVisible ? 'Show Less' : 'Show Full Token';
      }
    }

    function copyToken() {
      if (storedToken) {
        navigator.clipboard.writeText(storedToken).then(() => {
          const copyBtn = document.getElementById('copyBtn');
          const originalText = copyBtn.textContent;
          copyBtn.textContent = 'Copied!';
          copyBtn.classList.add('copied');
          
          setTimeout(() => {
            copyBtn.textContent = originalText;
            copyBtn.classList.remove('copied');
          }, 2000);
        }).catch(err => {
          console.error('Failed to copy token:', err);
          alert('Failed to copy token. Please select and copy manually.');
        });
      }
    }

    function redirectToLogin() {
      const redirectUri = encodeURIComponent(window.location.href);
      const wayjaLoginUrl = \`https://wayja.me/auth/login?redirect_uri=\${redirectUri}\`;
      window.location.href = wayjaLoginUrl;
    }
  </script>
</body>
</html>`, 'test-html')}
                  className="text-gray-400 hover:text-white"
                >
                  {copiedCode === 'test-html' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <pre className="whitespace-pre-wrap text-xs overflow-x-auto max-h-96 overflow-y-auto break-words overflow-wrap-anywhere">{`<!DOCTYPE html>
<html>
<head>
  <title>Wayja OAuth Test</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      max-width: 800px;
      margin: 50px auto;
      padding: 20px;
      background-color: #f5f5f5;
    }
    .container {
      background: white;
      padding: 30px;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    h1 {
      color: #333;
      margin-bottom: 20px;
    }
    #status {
      padding: 15px;
      background-color: #e3f2fd;
      border-left: 4px solid #2196f3;
      margin-bottom: 20px;
      border-radius: 4px;
    }
    #status strong {
      color: #1976d2;
    }
    button {
      background-color: #00b4a6;
      color: white;
      border: none;
      padding: 12px 24px;
      font-size: 16px;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.3s;
    }
    button:hover {
      background-color: #009688;
    }
    #tokenDisplay {
      margin-top: 30px;
      padding: 20px;
      background-color: #f9f9f9;
      border-radius: 4px;
      border: 1px solid #e0e0e0;
    }
    #tokenDisplay h3 {
      margin-top: 0;
      color: #333;
    }
    #tokenDisplay code {
      background-color: #f0f0f0;
      padding: 2px 6px;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      font-size: 14px;
    }
    .success {
      color: #4caf50;
      font-weight: bold;
    }
    .token-container {
      background-color: #f5f5f5;
      padding: 15px;
      border-radius: 4px;
      margin: 10px 0;
      word-break: break-all;
      position: relative;
    }
    .token-text {
      font-family: 'Courier New', monospace;
      font-size: 12px;
      color: #333;
      margin: 0;
      padding-right: 80px;
    }
    .copy-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      background-color: #00b4a6;
      color: white;
      border: none;
      padding: 6px 12px;
      font-size: 12px;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.3s;
    }
    .copy-btn:hover {
      background-color: #009688;
    }
    .copy-btn.copied {
      background-color: #4caf50;
    }
    .show-full-btn {
      background-color: #2196f3;
      color: white;
      border: none;
      padding: 6px 12px;
      font-size: 12px;
      border-radius: 4px;
      cursor: pointer;
      margin-top: 10px;
      transition: background-color 0.3s;
    }
    .show-full-btn:hover {
      background-color: #1976d2;
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>Wayja OAuth 2.0 Test</h1>
    
    <div id="status">Ready to authenticate...</div>
    <button id="loginBtn" onclick="redirectToLogin()">Login with Wayja</button>
    <div id="tokenDisplay"></div>
  </div>

  <script>
    let fullTokenVisible = false;
    let storedToken = null;
    let storedUserId = null;

    // Check if we're returning from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const userToken = urlParams.get('wayja_token');
    const userId = urlParams.get('wayja_user_id');

    if (userToken && userId) {
      storedToken = userToken;
      storedUserId = userId;
      displayToken(userToken, userId);
      
      // Clean URL (remove token for security)
      window.history.replaceState({}, '', window.location.pathname);
    }

    function displayToken(token, userId) {
      document.getElementById('status').innerHTML = 
        '<strong class="success">✓ Authentication successful!</strong>';
      
      const tokenDisplay = document.getElementById('tokenDisplay');
      tokenDisplay.innerHTML = 
        '<h3>Token Received:</h3>' +
        '<p><strong>User ID:</strong> ' + userId + '</p>' +
        '<div class="token-container">' +
        '<p class="token-text" id="tokenText">' + (fullTokenVisible ? token : token.substring(0, 50) + '...') + '</p>' +
        '<button class="copy-btn" onclick="copyToken()" id="copyBtn">Copy Token</button>' +
        '</div>' +
        '<button class="show-full-btn" onclick="toggleTokenDisplay()" id="toggleBtn">' + 
        (fullTokenVisible ? 'Show Less' : 'Show Full Token') + 
        '</button>' +
        '<p class="success" style="margin-top: 15px;">You can now use this token in API calls!</p>' +
        '<p style="margin-top: 15px; font-size: 14px; color: #666;">' +
        '<strong>Example API call:</strong><br>' +
        '<code style="display: block; margin-top: 5px; padding: 10px; background: #f0f0f0; border-radius: 4px; word-break: break-all;">' +
        'fetch("https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/wallet-balance?user_token=" + token, {<br>' +
        '&nbsp;&nbsp;headers: { "X-API-Key": "YOUR_API_KEY" }<br>' +
        '})' +
        '</code></p>';
    }

    function toggleTokenDisplay() {
      fullTokenVisible = !fullTokenVisible;
      const tokenText = document.getElementById('tokenText');
      const toggleBtn = document.getElementById('toggleBtn');
      
      if (storedToken) {
        tokenText.textContent = fullTokenVisible ? storedToken : storedToken.substring(0, 50) + '...';
        toggleBtn.textContent = fullTokenVisible ? 'Show Less' : 'Show Full Token';
      }
    }

    function copyToken() {
      if (storedToken) {
        navigator.clipboard.writeText(storedToken).then(() => {
          const copyBtn = document.getElementById('copyBtn');
          const originalText = copyBtn.textContent;
          copyBtn.textContent = 'Copied!';
          copyBtn.classList.add('copied');
          
          setTimeout(() => {
            copyBtn.textContent = originalText;
            copyBtn.classList.remove('copied');
          }, 2000);
        }).catch(err => {
          console.error('Failed to copy token:', err);
          alert('Failed to copy token. Please select and copy manually.');
        });
      }
    }

    function redirectToLogin() {
      const redirectUri = encodeURIComponent(window.location.href);
      const wayjaLoginUrl = \`https://wayja.me/auth/login?redirect_uri=\${redirectUri}\`;
      window.location.href = wayjaLoginUrl;
    }
  </script>
</body>
</html>`}</pre>
            </div>
            <div className="text-green-700 text-sm space-y-2">
              <p><strong>Testing Steps:</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Save the HTML code above to a file named <code className="bg-green-100 px-1 rounded">test-oauth.html</code></li>
                <li>Open the file in your browser (or serve it via a local web server)</li>
                <li><strong>Important:</strong> If you're already logged into Wayja, log out first or use an incognito/private window to test the full flow</li>
                <li>Click "Login with Wayja" button</li>
                <li>You'll be redirected to Wayja login page</li>
                <li>After logging in, you'll be redirected back with <code className="bg-green-100 px-1 rounded">wayja_token</code> and <code className="bg-green-100 px-1 rounded">wayja_user_id</code> in the URL</li>
                <li>The page will display the token - use this in your API calls!</li>
                <li><strong>Note:</strong> If you're already logged in, clicking the button will immediately redirect you back with your token (no login required)</li>
              </ol>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">Using the Token in API Calls</h4>
            <p className="text-blue-700 text-sm mb-2">
              Once you have the <code className="bg-blue-100 px-1 rounded">user_token</code>, use it in API requests:
            </p>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400">Complete Example with Error Handling</span>
                <button
                  onClick={() => copyToClipboard(`// Get wallet balance with error handling
async function getWalletBalance(userToken, apiKey) {
  try {
    // URL encode the user_token to handle special characters safely
    const encodedToken = encodeURIComponent(userToken);
    const url = \`https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/wallet-balance?user_token=\${encodedToken}\`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(\`API Error: \${errorData.error || response.statusText}\`);
    }

    const data = await response.json();
    
    if (data.success) {
      console.log('Wallet balance:', data.data.balance);
      console.log('Currency:', data.data.currency);
      return data.data;
    } else {
      throw new Error(data.error || 'Unknown error');
    }
  } catch (error) {
    console.error('Failed to fetch wallet balance:', error);
    throw error;
  }
}

// Usage
const userToken = 'your_wayja_token_here';
const apiKey = 'your_api_key_here';

getWalletBalance(userToken, apiKey)
  .then(balance => {
    console.log('Success! Balance:', balance);
  })
  .catch(error => {
    console.error('Error:', error.message);
  });`, 'wallet-balance-example')}
                  className="text-gray-400 hover:text-white"
                >
                  {copiedCode === 'wallet-balance-example' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="overflow-x-auto overflow-y-auto max-h-96">
                <pre className="whitespace-pre text-xs">{`// Get wallet balance with error handling
async function getWalletBalance(userToken, apiKey) {
  try {
    // URL encode the user_token to handle special characters safely
    const encodedToken = encodeURIComponent(userToken);
    const url = \`https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/wallet-balance?user_token=\${encodedToken}\`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(\`API Error: \${errorData.error || response.statusText}\`);
    }

    const data = await response.json();
    
    if (data.success) {
      console.log('Wallet balance:', data.data.balance);
      console.log('Currency:', data.data.currency);
      return data.data;
    } else {
      throw new Error(data.error || 'Unknown error');
    }
  } catch (error) {
    console.error('Failed to fetch wallet balance:', error);
    throw error;
  }
}

// Usage
const userToken = 'your_wayja_token_here';
const apiKey = 'your_api_key_here';

getWalletBalance(userToken, apiKey)
  .then(balance => {
    console.log('Success! Balance:', balance);
  })
  .catch(error => {
    console.error('Error:', error.message);
  });`}</pre>
              </div>
            </div>
            <div className="bg-blue-100 border border-blue-300 rounded-lg p-3">
              <p className="text-xs text-blue-900">
                <strong>💡 Tip:</strong> Replace <code className="bg-blue-200 px-1 rounded">your_wayja_token_here</code> with the token you received from the OAuth flow, 
                and <code className="bg-blue-200 px-1 rounded">your_api_key_here</code> with your developer API key. 
                The function includes error handling to help debug any issues.
              </p>
            </div>
          </div>

          <p className="text-gray-600">
            The <code className="bg-gray-100 px-1 rounded">user_token</code> is a signed session token issued by Wayja after successful authentication. 
            Use it in the request body for endpoints like <code className="bg-gray-100 px-1 rounded">place-bet</code> and <code className="bg-gray-100 px-1 rounded">wallet-balance</code>.
          </p>
        </div>
      )
    },
    {
      id: 5,
      title: "Place Your First Trade",
      description: "Implement betting functionality in your application.",
      icon: <Zap className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Now you're ready to place trades! Use the trade placement endpoint with user authentication and HMAC request signing:
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
              <div>
                <h4 className="font-medium text-yellow-800">HMAC Signing Required</h4>
                <p className="text-yellow-700 text-sm mt-1">
                  The <code className="bg-yellow-100 px-1 rounded">place-bet</code> endpoint requires HMAC request signing for security. 
                  You must include <code className="bg-yellow-100 px-1 rounded">X-Request-Timestamp</code>, <code className="bg-yellow-100 px-1 rounded">X-Request-Nonce</code>, 
                  and <code className="bg-yellow-100 px-1 rounded">X-Request-Signature</code> headers signed with your signing secret.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400">Place Trade (with HMAC Signing)</span>
              <button
                onClick={() => copyToClipboard(`TIMESTAMP=$(date +%s000)
NONCE=$(uuidgen)
BODY='{"option_id":123,"amount":100,"user_token":"wayja_token_here"}'
# Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
SIGNATURE=$(echo -n "POST\\n/place-bet\\n$TIMESTAMP\\n$NONCE\\n$BODY" | openssl dgst -sha256 -hmac "YOUR_SIGNING_SECRET" -binary | base64 | tr '+/' '-_' | tr -d '=')

curl -X POST https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/place-bet \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "X-Request-Timestamp: $TIMESTAMP" \\
  -H "X-Request-Nonce: $NONCE" \\
  -H "X-Request-Signature: $SIGNATURE" \\
  -d "$BODY"`, 'bet-curl')}
                className="text-gray-400 hover:text-white"
              >
                {copiedCode === 'bet-curl' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <pre className="whitespace-pre-wrap break-words overflow-wrap-anywhere overflow-x-auto">{`TIMESTAMP=$(date +%s000)
NONCE=$(uuidgen)
BODY='{"option_id":123,"amount":100,"user_token":"wayja_token_here"}'
# Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
SIGNATURE=$(echo -n "POST\\n/place-bet\\n$TIMESTAMP\\n$NONCE\\n$BODY" | openssl dgst -sha256 -hmac "YOUR_SIGNING_SECRET" -binary | base64 | tr '+/' '-_' | tr -d '=')

curl -X POST https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/place-bet \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "X-Request-Timestamp: $TIMESTAMP" \\
  -H "X-Request-Nonce: $NONCE" \\
  -H "X-Request-Signature: $SIGNATURE" \\
  -d "$BODY"`}</pre>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">HMAC Signature Generation</h4>
            <p className="text-sm text-blue-800 mb-2">
              Create the signature by concatenating: <code className="bg-blue-100 px-1 rounded">method\npath\ntimestamp\nnonce\nbody</code>
            </p>
            <p className="text-sm text-blue-800">
              Then sign with HMAC-SHA256 using your signing secret. See the <Link to="/developers/api-reference" className="underline font-medium">API Reference</Link> for detailed examples.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
              <div>
                <h4 className="font-medium text-green-800">Success!</h4>
                <p className="text-green-700 text-sm mt-1">
                  You've successfully integrated Wayja's prediction market functionality into your application. 
                  Remember: state-modifying endpoints require HMAC signing, while read-only endpoints (like market-data) only need API key authentication.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = steps.find(step => step.id === currentStep);

  return (
    <div className="min-h-screen bg-wayja-cream">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-wayja-black">Quick Start Guide</h1>
              <p className="text-wayja-dark-slate mt-2">Get up and running with Wayja's API in under 10 minutes</p>
            </div>
            <Link
              to="/developers"
              className="text-wayja-teal hover:text-wayja-dark-teal font-medium"
            >
              ← Back to Developer Portal
            </Link>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-2">
            {steps.map((step) => (
              <React.Fragment key={step.id}>
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                    step.id <= currentStep
                      ? 'bg-wayja-teal text-white'
                      : 'bg-wayja-gray text-wayja-dark-slate'
                  }`}
                >
                  {step.id < currentStep ? <CheckCircle className="w-4 h-4" /> : step.id}
                </div>
                {step.id < steps.length && (
                  <div
                    className={`flex-1 h-1 ${
                      step.id < currentStep ? 'bg-wayja-teal' : 'bg-wayja-gray'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-4">
            <h2 className="text-xl font-semibold text-wayja-black">{currentStepData?.title}</h2>
            <p className="text-wayja-dark-slate mt-1">{currentStepData?.description}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1 max-w-4xl">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="flex items-center mb-6">
                <div className="bg-wayja-light-teal/30 p-3 rounded-lg mr-4">
                  {currentStepData?.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-wayja-black">Step {currentStep}: {currentStepData?.title}</h3>
                </div>
              </div>

              {currentStepData?.getContent ? currentStepData.getContent() : currentStepData?.content}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  currentStep === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Previous Step
              </button>

              <div className="flex space-x-3">
                <Link
                  to="/developers/api-reference"
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  <Book className="w-4 h-4 inline mr-2" />
                  API Reference
                </Link>

                {currentStep < steps.length ? (
                  <button
                    onClick={nextStep}
                    className="px-6 py-3 bg-wayja-teal text-white rounded-lg font-medium hover:bg-wayja-dark-teal transition-colors"
                  >
                    Next Step
                    <ArrowRight className="w-4 h-4 inline ml-2" />
                  </button>
                ) : (
                  <Link
                    to="/developers/examples"
                    className="px-6 py-3 bg-wayja-teal text-white rounded-lg font-medium hover:bg-wayja-dark-teal transition-colors"
                  >
                    View Examples
                    <ExternalLink className="w-4 h-4 inline ml-2" />
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
              <div className="space-y-3">
                <Link
                  to="/developers/api-reference"
                  className="block p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="font-medium text-gray-900">API Reference</div>
                  <div className="text-sm text-gray-600">Complete endpoint documentation</div>
                </Link>

                <Link
                  to="/developers/examples"
                  className="block p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="font-medium text-gray-900">Code Examples</div>
                  <div className="text-sm text-gray-600">Ready-to-use code samples</div>
                </Link>

                <a
                  href="mailto:support@wayja.me"
                  className="block p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="font-medium text-gray-900">Contact Support</div>
                  <div className="text-sm text-gray-600">Get help from our team</div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickStart;
